import { createFileRoute } from "@tanstack/react-router";
import { MarketCard, type TickerFor } from "@/components/markets/MarketCard";
import { MarketCardSkeleton } from "@/components/markets/MarketCardSkeleton";
import type { MarketGroup } from "@/lib/queries";
import type { MarketItem, TickerSnapshot } from "@/lib/types";
import { buildHead } from "@/lib/seo";

// Dev-only preview: renders MarketCard with mock data so the card design can be
// verified without a running backend. Not linked from the app.
export const Route = createFileRoute("/markets-preview")({
  head: () => buildHead({ path: "/markets-preview", title: "Markets preview (dev)", noindex: true }),
  component: Preview,
});

const YESNO = [
  { outcome_id: "y", market_id: "", code: "YES", name: "Yes", display_order: 0 },
  { outcome_id: "n", market_id: "", code: "NO", name: "No", display_order: 1 },
];

const DIRECTION = [
  { outcome_id: "higher", market_id: "", code: "YES", name: "Higher", display_order: 0 },
  { outcome_id: "lower-flat", market_id: "", code: "NO", name: "Lower / Flat", display_order: 1 },
];

const mkMarket = (market_id: string, name: string, outcomes = YESNO): MarketItem =>
  ({
    market_id,
    name,
    category_id: "c",
    status: "OPEN",
    tick_size: "1",
    lot_size: "1",
    min_amount: "1",
    price_min: "0",
    price_max: "10000",
    price_precision: 4,
    amount_precision: 0,
    maker_fee_rate: "0",
    taker_fee_rate: "0",
    fee_rate_precision: 4,
    outcomes,
  }) as unknown as MarketItem;

// best_bid/ask are minor units (precision 4); pillPricesFor divides by 100 →
// cents. chance 62% ⇒ yes ask cents 62 ⇒ minor 6200.
const ticker = (bidCents: number, askCents: number): TickerSnapshot =>
  ({
    market_id: "",
    sequence: 1,
    last_price: String(askCents * 100),
    best_bid: String(bidCents * 100),
    best_ask: String(askCents * 100),
    mid_price: String(((bidCents + askCents) / 2) * 100),
  }) as unknown as TickerSnapshot;

const TICKERS: Record<string, TickerSnapshot> = {
  btc: ticker(61, 62),
  eth: ticker(37, 38),
  "fr-a": ticker(43, 45),
  "fr-b": ticker(26, 28),
  "fr-c": ticker(16, 18),
  "fr-d": ticker(8, 10),
};
const tickerFor: TickerFor = (id) => TICKERS[id];

const GROUPS: MarketGroup[] = [
  {
    id: "btc",
    title: "Will Bitcoin close above $100,000 by Dec 31, 2026?",
    category_id: "crypto",
    category_name: "Crypto",
    markets: [mkMarket("btc", "BTC > 100k")],
    multi: false,
    kind: "binary",
  },
  {
    id: "fr",
    title: "Who will be the next Prime Minister of France?",
    category_id: "politics",
    category_name: "Politics",
    markets: [
      mkMarket("fr-a", "Édouard Philippe"),
      mkMarket("fr-b", "Gérald Darmanin"),
      mkMarket("fr-c", "Marine Le Pen"),
      mkMarket("fr-d", "Jordan Bardella"),
    ],
    multi: true,
    kind: "categorical",
    markets_total: 8,
  },
  {
    id: "eth",
    title: "Will SET50 close higher on Monday?",
    category_id: "stocks",
    category_name: "Stocks",
    markets: [mkMarket("eth", "SET50 direction", DIRECTION)],
    multi: false,
    kind: "binary",
  },
];

function Preview() {
  return (
    <div className="min-h-dvh bg-bg p-6 text-fg">
      <h1 className="mb-4 font-display text-2xl font-bold">MarketCard preview (mock data)</h1>
      {/* Mirror the real home feed grid and its image-led card variant. This
          route deliberately leaves icon_url empty so the missing-image state
          stays testable without a backend or external image host. */}
      <div className="grid w-full grid-cols-1 gap-3 xl:grid-cols-2 xl:gap-4 2xl:grid-cols-4">
        {GROUPS.map((g) => (
          <MarketCard key={g.id} group={g} tickerFor={tickerFor} imageLed />
        ))}
        <MarketCardSkeleton imageLed />
      </div>
    </div>
  );
}
